import { useCallback, useEffect, useMemo, useState, type MouseEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom'
import dayjs from 'dayjs'
import type { Dayjs } from 'dayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar'
import 'dayjs/locale/ru'
import 'dayjs/locale/en'
import { lessonsApi } from '@/api/lessons'
import { LessonRescheduledChip } from '@/components/lessons/LessonRescheduledChip'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import type { Lesson, LessonStatus, UserRef } from '@/types'
import { ROUTES } from '@/utils/constants'
import '@/styles/dashboard.css'
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Checkbox,
  Chip,
  Collapse,
  FormControl,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Popover,
  Select,
  SelectChangeEvent,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material'
import { Home as HomeIcon, NavigateNext as NavigateNextIcon, Visibility as VisibilityIcon } from '@mui/icons-material'
import { useTheme } from '@mui/material/styles'
import { getErrorMessage } from '@/utils/error'
import { formatLessonTimeRange } from '@/utils/date'
import { PageHelpPopover } from '@/components/common/PageHelpPopover'
import { FiltersButton } from '@/components/common/FiltersButton'
import { PaymentStatusChip } from '@/components/billing/PaymentStatusChip'
import { useAuthStore } from '@/stores/authStore'
import {
  useStudentLessonsListRealtime,
  type LessonStatusChangedPayload,
} from '@/realtime/useLessonRealtime'

const statusOrder: LessonStatus[] = ['planned', 'in_progress', 'completed', 'canceled']

function formatTeacher(teacher?: UserRef | null): string {
  if (!teacher) return '—'
  return (
    teacher.display_name?.trim() ||
    [teacher.surname, teacher.first_name].filter((p): p is string => Boolean(p?.trim())).join(' ').trim() ||
    teacher.email ||
    `#${teacher.id}`
  )
}

/**
 * Список уроков ученика: те же фильтры по дате/статусу, что у учителя, без выбора владельца.
 * API: `GET /lessons?as_student=1` — только уроки с участием текущего пользователя
 * (обязательно при роли teacher+student, иначе бэкенд отдаёт список «как преподаватель»).
 */
export function StudentLessonsPage() {
  const { t, i18n } = useTranslation()
  const roles = useAuthStore((s) => s.user?.roles) ?? []
  const isStudent = roles.some((r) => r.slug === 'student')
  const isTeacher = roles.some((r) => r.slug === 'teacher')
  const showRoleTabs = isStudent && isTeacher
  const [, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const theme = useTheme()
  const menuItemSelectedSx = {
    '&.Mui-selected': {
      color: theme.palette.primary.contrastText,
    },
  }
  const adapterLocale = i18n.language === 'ru' ? 'ru' : 'en'

  const [lessons, setLessons] = useState<Lesson[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [fromDate, setFromDate] = useState<string>('')
  const [toDate, setToDate] = useState<string>('')
  const [statusFilter, setStatusFilter] = useState<LessonStatus[]>(['planned', 'in_progress'])

  const [filtersOpen, setFiltersOpen] = useState(false)
  const [dateRangeAnchorEl, setDateRangeAnchorEl] = useState<HTMLElement | null>(null)
  const [page, setPage] = useState(0)
  const [perPage, setPerPage] = useState(50)
  const [total, setTotal] = useState(0)

  const appliedFiltersCount = useMemo(() => {
    let count = 0
    if (statusFilter.length > 0) count += 1
    if (fromDate || toDate) count += 1
    return count
  }, [fromDate, statusFilter, toDate])

  const hasActiveFilters = appliedFiltersCount > 0

  const statusLabel = (s: LessonStatus) => {
    if (s === 'planned') return t('lessons.status.planned')
    if (s === 'in_progress') return t('lessons.status.in_progress')
    if (s === 'completed') return t('lessons.status.completed')
    return t('lessons.status.canceled')
  }

  const openDateRange = (e: MouseEvent<HTMLElement>) => {
    setDateRangeAnchorEl(e.currentTarget)
  }

  const closeDateRange = () => {
    setDateRangeAnchorEl(null)
  }

  const dateCalendarValue: Dayjs | null = toDate ? dayjs(toDate) : fromDate ? dayjs(fromDate) : null

  const dateRangeLabel = useMemo(() => {
    if (fromDate && toDate) {
      return `${dayjs(fromDate).format('DD.MM.YYYY')} - ${dayjs(toDate).format('DD.MM.YYYY')}`
    }
    if (fromDate) {
      return `с ${dayjs(fromDate).format('DD.MM.YYYY')}`
    }
    if (toDate) {
      return `до ${dayjs(toDate).format('DD.MM.YYYY')}`
    }
    return ''
  }, [fromDate, toDate])

  const handleDateCalendarChange = (value: Dayjs | null) => {
    if (!value) return
    const picked = value.format('YYYY-MM-DD')

    if (!fromDate || toDate) {
      setFromDate(picked)
      setToDate('')
      return
    }

    if (picked < fromDate) {
      setFromDate(picked)
      setToDate(fromDate)
    } else {
      setToDate(picked)
    }
    closeDateRange()
  }

  useEffect(() => {
    setPage(0)
  }, [fromDate, statusFilter, toDate])

  const fetchLessons = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await lessonsApi.index({
        from: fromDate || undefined,
        to: toDate || undefined,
        status: statusFilter.length ? statusFilter : undefined,
        per_page: perPage,
        page: page + 1,
        as_student: true,
      })
      setLessons(response.data)
      setTotal(response.total ?? response.data.length)
    } catch (e: unknown) {
      setError(getErrorMessage(e) || t('fallback.error', 'Ошибка'))
    } finally {
      setLoading(false)
    }
  }, [fromDate, page, perPage, statusFilter, toDate, t])

  useEffect(() => {
    fetchLessons().catch(() => {})
  }, [fetchLessons])

  /**
   * Событие несёт статус и actual_start_at, поэтому строка обновляется сразу:
   * ученик видит начавшийся урок, не дожидаясь ответа на повторный запрос
   * списка (и даже если тот вернёт ещё не обновлённые данные).
   */
  const applyLessonStatusEvent = useCallback((payload: LessonStatusChangedPayload) => {
    setLessons((prev) =>
      prev.map((lesson) =>
        lesson.id === payload.lesson_id
          ? {
              ...lesson,
              status: payload.status,
              actual_start_at: payload.actual_start_at,
              actual_end_at: payload.actual_end_at,
            }
          : lesson,
      ),
    )
  }, [])

  /*
   * Список ученика тоже подписан на каналы уроков. Без этого старт урока до
   * ученика не доходил совсем: данные грузятся один раз, поллинга нет, а
   * подписка со списка была только у преподавателя.
   *
   * Канал — приватный `lesson.{id}.status`, а не presence урока: presence
   * означает «ученик на занятии», и открытый список записывал ему присутствие
   * на уроке, куда он не заходил.
   */
  useStudentLessonsListRealtime(lessons, fetchLessons, applyLessonStatusEvent)

  const handleStatusChange = (e: SelectChangeEvent<typeof statusFilter>) => {
    const value = e.target.value
    if (typeof value === 'string') {
      setStatusFilter(value ? ([value] as LessonStatus[]) : [])
      return
    }
    setStatusFilter(value as LessonStatus[])
  }

  return (
    <DashboardLayout>
      <Box sx={{ p: { xs: 2, sm: 3 }, pb: 3 }}>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb" sx={{ mb: 2 }}>
          <Link component={RouterLink} to={ROUTES.DASHBOARD} underline="hover" sx={{ display: 'flex', alignItems: 'center' }}>
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            {t('dashboard.home')}
          </Link>
          <Typography color="text.primary">{t('lessons.title')}</Typography>
        </Breadcrumbs>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 1 }}>
          <Typography variant="h4" component="h1" fontWeight={700} sx={{ flexGrow: 1 }}>
            {t('lessons.title')}
          </Typography>
          <PageHelpPopover pageKey="lessons-student" />
        </Box>
        {showRoleTabs && (
          <Tabs
            value="student"
            onChange={(_, v: string) => setSearchParams({ tab: v })}
            sx={{ mb: 1 }}
          >
            <Tab value="student" label={t('dashboard.student', 'Ученик')} />
            <Tab value="teaching" label={t('dashboard.teacher', 'Преподаватель')} />
          </Tabs>
        )}
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          {t('lessons.studentSubtitle')}
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <FiltersButton
            open={filtersOpen}
            count={appliedFiltersCount}
            onToggle={() => setFiltersOpen((o) => !o)}
            label={t('lessons.filtersButton', 'Фильтры')}
          />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Collapse in={filtersOpen}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2, px: 1, py: 1 }}>
            <TextField
              size="small"
              label={t('lessons.filterPeriod', 'Период')}
              value={dateRangeLabel}
              InputProps={{ readOnly: true }}
              onClick={openDateRange}
              sx={{ minWidth: { xs: '100%', sm: 220 }, cursor: 'pointer' }}
            />

            <Popover
              open={Boolean(dateRangeAnchorEl)}
              anchorEl={dateRangeAnchorEl}
              onClose={closeDateRange}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
              <Box sx={{ p: 1 }}>
                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={adapterLocale}>
                  <DateCalendar
                    value={dateCalendarValue}
                    views={['month', 'day']}
                    onChange={(v) => void handleDateCalendarChange(v)}
                  />
                </LocalizationProvider>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => {
                      setFromDate('')
                      setToDate('')
                      closeDateRange()
                    }}
                  >
                    {t('common.reset', 'Сбросить')}
                  </Button>
                  <Button size="small" variant="contained" color="secondary" onClick={closeDateRange}>
                    {t('common.ok', 'Ок')}
                  </Button>
                </Box>
              </Box>
            </Popover>

            <FormControl size="small" sx={{ minWidth: 240 }}>
              <InputLabel>{t('lessons.filterStatus', 'Статус')}</InputLabel>
              <Select
                multiple
                value={statusFilter}
                label={t('lessons.filterStatus', 'Статус')}
                onChange={handleStatusChange}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(selected as LessonStatus[]).map((s) => (
                      <Chip key={s} size="small" label={statusLabel(s)} />
                    ))}
                  </Box>
                )}
              >
                {statusOrder.map((s) => (
                  <MenuItem key={s} value={s} sx={menuItemSelectedSx}>
                    <Checkbox
                      size="small"
                      checked={statusFilter.includes(s)}
                      sx={{
                        mr: 1,
                        color: 'inherit',
                        '&.Mui-checked': { color: 'inherit' },
                      }}
                    />
                    {statusLabel(s)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Collapse>

        {loading ? (
          <Box sx={{ width: '100%' }}>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow hover={false} sx={{ '&:hover': { backgroundColor: 'transparent' } }}>
                    {Array.from({ length: 6 }).map((_, idx) => (
                      <TableCell key={idx}>
                        <Skeleton variant="text" width={idx === 0 ? 120 : 100} />
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Array.from({ length: 5 }).map((_, rowIdx) => (
                    <TableRow key={rowIdx}>
                      {Array.from({ length: 6 }).map((__, colIdx) => (
                        <TableCell key={colIdx}>
                          <Skeleton variant="text" width={colIdx === 4 ? 90 : 120} />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow hover={false} sx={{ '&:hover': { backgroundColor: 'transparent' } }}>
                  <TableCell>{t('lessons.tablePlannedAt', 'Дата урока')}</TableCell>
                  <TableCell>{t('lessons.tableTeacher', 'Преподаватель')}</TableCell>
                  <TableCell>{t('lessons.tableScope', 'Формат')}</TableCell>
                  <TableCell>{t('lessons.tableStatus', 'Статус')}</TableCell>
                  <TableCell>{t('billing.paymentStatusLabel', 'Оплата')}</TableCell>
                  <TableCell align="right">{t('common.actions', 'Действия')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lessons.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography color="text.secondary">
                        {hasActiveFilters
                          ? t('lessons.emptyByFilters', 'По вашим фильтрам ничего не найдено, ослабьте фильтры')
                          : t('lessons.empty', 'Уроки не найдены')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  lessons.map((lesson) => {
                    const scopeLabel =
                      lesson.lesson_scope === 'individual'
                        ? t('lessons.scopeIndividual', 'Индивидуальный')
                        : t('lessons.scopeGroup', 'Групповой')

                    return (
                      <TableRow key={lesson.id}>
                        <TableCell>
                          {/* Дата открывает урок — кнопка «глаз» справа остаётся:
                              ученик чаще целится в дату, чем ищет иконку в конце
                              строки */}
                          <Link
                            component={RouterLink}
                            to={ROUTES.LESSON_DETAILS(lesson.id)}
                            underline="hover"
                            sx={{ fontSize: 14 }}
                          >
                            {formatLessonTimeRange(lesson.planned_start_at, lesson.planned_end_at)}
                          </Link>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{formatTeacher(lesson.teacher)}</Typography>
                        </TableCell>
                        <TableCell>{scopeLabel}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                            {statusLabel(lesson.status)}
                            <LessonRescheduledChip lesson={lesson} />
                          </Box>
                        </TableCell>
                        <TableCell>
                          <PaymentStatusChip status={lesson.payment_status ?? undefined} />
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            aria-label={t('lessons.details', 'Детали')}
                            onClick={() => navigate(ROUTES.LESSON_DETAILS(lesson.id))}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    )
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {!loading && total > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', mt: 2 }}>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={perPage}
              onRowsPerPageChange={(e) => {
                setPerPage(parseInt(e.target.value, 10))
                setPage(0)
              }}
              rowsPerPageOptions={[25, 50, 100]}
              labelRowsPerPage={t('words.rows_per_page', { defaultValue: 'На странице' })}
              labelDisplayedRows={({ from, to, count }) =>
                `${from}–${to} ${t('words.of', { defaultValue: 'из' })} ${count !== -1 ? count : to}`
              }
              sx={{ flex: 1, minWidth: 0, overflow: 'auto' }}
            />
          </Box>
        )}
      </Box>
    </DashboardLayout>
  )
}
